/**
 * SocialFollowRow — "Follow Storylo" buttons for Instagram, Facebook and
 * YouTube. Shown under every story player and on the public homepage so
 * every viewer gets a one-tap path to becoming a follower.
 */
import React from "react";

export const INSTAGRAM_URL = "https://www.instagram.com/storylo.co.uk";
export const FACEBOOK_URL = "https://www.facebook.com/profile.php?id=61588744511974";
export const YOUTUBE_URL =
  "https://www.youtube.com/@Storylo-where-stories-flow?sub_confirmation=1";

const btnBase =
  "group inline-flex items-center gap-2.5 pl-3 pr-4 py-2 rounded-full transition-transform hover:scale-[1.025] active:scale-[0.99]";

export default function SocialFollowRow({ compact = false }) {
  return (
    <div
      className="rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3.5"
      data-testid="social-follow-row"
    >
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] mb-2.5 flex items-center gap-2">
        <span
          aria-hidden="true"
          className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--accent)]"
        />
        Follow Storylo · new stories & behind-the-scenes
      </div>
      <div className={`flex flex-wrap items-center gap-2.5 ${compact ? "" : "md:gap-3"}`}>
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={btnBase}
          style={{
            background:
              "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
            color: "#fff",
            boxShadow:
              "0 1px 0 rgba(255,255,255,0.18) inset, 0 6px 20px -8px rgba(220, 39, 67, 0.55)",
          }}
          data-testid="follow-instagram"
          aria-label="Follow Storylo on Instagram"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zm0 10.162a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
          </svg>
          <span className="text-sm font-semibold tracking-tight">@storylo.co.uk</span>
        </a>

        <a
          href={FACEBOOK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={btnBase}
          style={{
            background: "#1877F2",
            color: "#fff",
            boxShadow:
              "0 1px 0 rgba(255,255,255,0.18) inset, 0 6px 20px -8px rgba(24, 119, 242, 0.55)",
          }}
          data-testid="follow-facebook"
          aria-label="Follow Storylo on Facebook"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
          <span className="text-sm font-semibold tracking-tight">Storylo</span>
        </a>

        <a
          href={YOUTUBE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={btnBase}
          style={{
            background: "#FF0000",
            color: "#fff",
            boxShadow:
              "0 1px 0 rgba(255,255,255,0.18) inset, 0 6px 20px -8px rgba(255, 0, 0, 0.5)",
          }}
          data-testid="follow-youtube"
          aria-label="Subscribe to Storylo on YouTube"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
          </svg>
          <span className="text-sm font-semibold tracking-tight">Subscribe</span>
        </a>
      </div>
    </div>
  );
}
